import { selectActiveEntry, usePollingStore } from '@shared/store/use-polling-store'
import { Link, useNavigate } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'motion/react'
import { HiMiniChevronRight, HiMiniUserCircle, HiMiniXMark } from 'react-icons/hi2'

interface FullscreenMenuProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly isLoggedIn?: boolean
  readonly userName?: string
}

export function FullscreenMenu({
  isOpen,
  onClose,
  isLoggedIn = false,
  userName = '회원',
}: FullscreenMenuProps) {
  const activeEntry = usePollingStore((s) => selectActiveEntry(s.entries))
  const navigate = useNavigate()

  const handleNavigateToResult = () => {
    if (!activeEntry) {
      return
    }
    onClose()
    if (activeEntry.status === 'completed' || activeEntry.status === 'viewed') {
      void navigate({
        to: '/saju/$sajuType/result',
        params: { sajuType: 'classic' },
        search: { hashKey: activeEntry.hashKey },
      })
    } else if (activeEntry.status === 'polling') {
      void navigate({
        to: '/saju/$sajuType',
        params: { sajuType: 'classic' },
      })
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-menu mx-auto flex max-w-mobile-shell flex-col"
          style={{ backgroundColor: 'hsl(var(--bg-base))' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Header */}
          <header className="flex h-14 items-center justify-end px-4">
            <button
              type="button"
              className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/30"
              aria-label="닫기"
              onClick={onClose}
            >
              <HiMiniXMark className="text-2xl" />
            </button>
          </header>

          {/* Primary Menu */}
          <nav className="flex flex-1 flex-col gap-3 px-6 pt-8">
            {/* 분석 상태 배너 */}
            {activeEntry && (
              <motion.button
                type="button"
                onClick={handleNavigateToResult}
                className="flex items-center gap-3 rounded-xl bg-bg-surface px-5 py-4 text-left transition-colors hover:bg-bg-surface-hover"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                <span className="flex-1 typo-label font-medium text-foreground">
                  {activeEntry.sajuName}
                  {activeEntry.subjectName ? `(${activeEntry.subjectName})` : ''}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 typo-footnote font-medium ${
                    activeEntry.status === 'polling'
                      ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                      : 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                  }`}
                >
                  {activeEntry.status === 'polling'
                    ? '분석중'
                    : activeEntry.status === 'completed'
                      ? '분석완료'
                      : '결과보기'}
                </span>
                <HiMiniChevronRight className="text-lg text-muted-foreground/40" />
              </motion.button>
            )}
            <Link
              to="/landing"
              className="py-4 text-left typo-heading font-semibold text-foreground transition-colors hover:text-primary"
              onClick={onClose}
            >
              <motion.span
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                서비스 소개
              </motion.span>
            </Link>
            <motion.span
              className="cursor-not-allowed py-4 text-left typo-heading font-semibold text-muted-foreground/40"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.05 }}
            >
              다른 풀이 테마
            </motion.span>
          </nav>

          {/* Bottom */}
          <div className="flex flex-col gap-4 px-6 pb-10">
            {/* Profile → MyPage */}
            <Link
              to="/mypage"
              className="flex items-center gap-3 rounded-xl bg-bg-surface px-5 py-4 transition-colors hover:bg-bg-surface-hover"
              onClick={onClose}
            >
              <HiMiniUserCircle className="shrink-0 text-4xl text-muted-foreground" />
              <span className="flex-1 typo-label font-medium text-foreground">
                {isLoggedIn ? userName : '손님'}
              </span>
              <HiMiniChevronRight className="text-lg text-muted-foreground/40" />
            </Link>

            {/* Secondary links */}
            <div className="flex justify-between">
              <Link
                to="/terms"
                className="typo-footnote text-muted-foreground/50 underline transition-colors hover:text-muted-foreground"
                onClick={onClose}
              >
                이용약관
              </Link>
              <Link
                to="/privacy"
                className="typo-footnote text-muted-foreground/50 underline transition-colors hover:text-muted-foreground"
                onClick={onClose}
              >
                개인정보처리방침
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
